
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/brand/logo";
import { usePwaInstall } from "@/hooks/use-pwa-install";
import { useUiStore } from "@/store/ui";
import { toast } from "sonner";
import {
  Smartphone,
  Download,
  RefreshCw,
  Trash2,
  Info,
  ExternalLink,
  Wifi,
  WifiOff,
  ServerCog,
  MonitorSmartphone,
  CheckCircle2,
  Loader2,
  Globe,
  ChevronRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";

// ============================================================
// APP_VERSION — single source of truth for the client build.
// Bump when shipping a new release. The About card + "Check for
// Updates" flow both read this.
// ============================================================
export const APP_VERSION = "1.0.0";

// ============================================================
// AppManagementCard — Install / Update / Clear Cache + status
// Matches the hl-app.png reference design.
// ============================================================
export function AppManagementCard() {
  const { t } = useTranslation();
  const { canInstall, isIos, isStandalone, promptInstall, dismiss } = usePwaInstall();
  const [checking, setChecking] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // Live online/offline + service-worker status.
  const [online, setOnline] = useState(true);
  const [swActive, setSwActive] = useState(false);

  useEffect(() => {
    setOnline(navigator.onLine);
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    // Check if a service worker is controlling this page.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        setSwActive(!!reg);
        // Listen for a waiting worker → update available.
        if (reg) {
          reg.addEventListener("updatefound", () => {
            const newWorker = reg.installing;
            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                  setUpdateAvailable(true);
                }
              });
            }
          });
        }
      });
    }

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const handleInstall = async () => {
    if (isStandalone) {
      toast.info(t("settings.appAlreadyInstalled"));
      return;
    }
    if (isIos) {
      toast.info(t("settings.installOnIosDesc"));
      return;
    }
    setInstalling(true);
    const accepted = await promptInstall();
    setInstalling(false);
    if (accepted) {
      toast.success(t("settings.installed"));
    } else {
      dismiss();
    }
  };

  const handleCheckUpdates = async () => {
    setChecking(true);
    try {
      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          await reg.update();
          // Give the browser a moment to detect a new worker.
          await new Promise((r) => setTimeout(r, 800));
          const fresh = await navigator.serviceWorker.getRegistration();
          if (fresh?.waiting) {
            setUpdateAvailable(true);
            toast.success(t("settings.updateAvailable"));
            fresh.waiting.postMessage({ type: "SKIP_WAITING" });
            setTimeout(() => window.location.reload(), 1000);
          } else {
            toast.success(t("settings.latestVersion"));
          }
        } else {
          toast.info(t("settings.noSwRegistered"));
        }
      } else {
        toast.info(t("settings.swUnsupported"));
      }
    } catch {
      toast.error(t("settings.updateCheckFailed"));
    } finally {
      setChecking(false);
    }
  };

  const handleClearCache = async () => {
    setClearing(true);
    try {
      // Clear all Cache API entries (keeps localStorage/IndexedDB user data).
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      toast.success(t("settings.cacheCleared"));
      setTimeout(() => window.location.reload(), 800);
    } catch {
      toast.error(t("settings.cacheClearFailed"));
    } finally {
      setClearing(false);
    }
  };

  // Display mode label.
  const displayMode = isStandalone ? t("settings.installedBadge") : t("settings.browser");

  return (
    <Card>
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border/60 p-5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Smartphone className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-bold">{t("settings.appManagement")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("settings.appManagementDesc")}
            </p>
          </div>
        </div>

        {/* Row 1: Install App */}
        <div className="flex items-center gap-3 border-b border-border/60 p-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
            <Download className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">{t("settings.installApp")}</p>
            <p className="text-xs text-muted-foreground">
              {t("settings.installAppDesc")}
            </p>
          </div>
          {isStandalone ? (
            <Badge className="shrink-0 gap-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              <CheckCircle2 className="h-3.5 w-3.5" /> {t("settings.installedBadge")}
            </Badge>
          ) : (
            <Button
              onClick={handleInstall}
              disabled={installing || (!canInstall && !isIos)}
              className="shrink-0 gap-1.5 bg-amber-500 text-amber-950 hover:bg-amber-600"
            >
              {installing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {installing ? t("settings.installing") : t("settings.installApp")}
            </Button>
          )}
        </div>

        {/* Row 2: Check for Updates */}
        <div className="flex items-center gap-3 border-b border-border/60 p-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
            <RefreshCw className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">{t("settings.checkForUpdates")}</p>
            <p className="text-xs text-muted-foreground">
              {t("settings.checkForUpdatesDesc")}
            </p>
            {updateAvailable && (
              <p className="mt-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">
                {t("settings.updateAvailableDesc")}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            onClick={handleCheckUpdates}
            disabled={checking}
            className="shrink-0 gap-1.5"
          >
            {checking ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {checking ? t("settings.checking") : t("settings.checkNow")}
          </Button>
        </div>

        {/* Row 3: Clear App Cache */}
        <div className="flex items-center gap-3 border-b border-border/60 p-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
            <Trash2 className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">{t("settings.clearCache")}</p>
            <p className="text-xs text-muted-foreground">
              {t("settings.clearCacheDesc")}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleClearCache}
            disabled={clearing}
            className="shrink-0 gap-1.5"
          >
            {clearing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            {clearing ? t("settings.clearing") : t("settings.clear")}
          </Button>
        </div>

        {/* Status pills */}
        <div className="grid grid-cols-3 gap-2 p-5">
          <StatusPill
            icon={swActive ? ServerCog : ServerCog}
            label={t("settings.serviceWorker")}
            value={swActive ? t("settings.swActive") : t("settings.swInactive")}
            tone={swActive ? "green" : "neutral"}
          />
          <StatusPill
            icon={online ? Wifi : WifiOff}
            label={t("settings.connection")}
            value={online ? t("sync.online") : t("sync.offline")}
            tone={online ? "green" : "red"}
          />
          <StatusPill
            icon={MonitorSmartphone}
            label={t("settings.displayMode")}
            value={displayMode}
            tone={isStandalone ? "blue" : "neutral"}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function StatusPill({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Wifi;
  label: string;
  value: string;
  tone: "green" | "red" | "blue" | "neutral";
}) {
  const toneCls = {
    green: "bg-emerald-50 dark:bg-emerald-950/40",
    red: "bg-red-50 dark:bg-red-950/40",
    blue: "bg-blue-50 dark:bg-blue-950/40",
    neutral: "bg-muted",
  }[tone];
  const iconCls = {
    green: "text-emerald-600 dark:text-emerald-400",
    red: "text-red-600 dark:text-red-400",
    blue: "text-blue-600 dark:text-blue-400",
    neutral: "text-muted-foreground",
  }[tone];
  return (
    <div className={`flex flex-col items-center gap-1 rounded-xl border border-border/60 p-3 text-center ${toneCls}`}>
      <Icon className={`h-5 w-5 ${iconCls}`} />
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-sm font-bold text-foreground">{value}</span>
    </div>
  );
}

// ============================================================
// AboutCard — app identity, version, legal links.
// Matches the hl-about.png reference design.
// ============================================================
export function AboutCard() {
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();

  const legalLinks = [
    { labelKey: "settings.privacyPolicy", route: "legal/privacy", icon: ExternalLink },
    { labelKey: "settings.termsOfService", route: "legal/terms", icon: ExternalLink },
    { labelKey: "settings.dataProtection", route: "legal/data-protection", icon: ExternalLink },
  ];

  return (
    <Card>
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border/60 p-5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Info className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-bold">{t("settings.about")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("settings.aboutDesc")}
            </p>
          </div>
        </div>

        {/* Brand block */}
        <div className="flex items-center gap-4 border-b border-border/60 p-5">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary/5 ring-1 ring-border/60">
            <Logo className="h-12 w-12" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-xl font-bold">{t("common.appName")}</h3>
            <p className="text-sm text-muted-foreground">
              {t("settings.brandTagline")}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-foreground">
                v{APP_VERSION}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground">
                <Globe className="h-3 w-3 text-muted-foreground" />
                {t("settings.hornOfAfrica")}
              </span>
            </div>
          </div>
        </div>

        {/* Body paragraph */}
        <div className="border-b border-border/60 p-5">
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t("settings.aboutParagraph")}
          </p>
        </div>

        {/* Legal document buttons */}
        <div className="grid grid-cols-1 gap-2 border-b border-border/60 p-5 sm:grid-cols-3">
          {legalLinks.map((link) => {
            const Icon = link.icon;
            return (
              <button
                key={link.route}
                onClick={() => navigate(link.route)}
                className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2.5 text-sm font-medium transition hover:border-primary/40 hover:bg-muted/50"
              >
                <span className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-primary" />
                  {t(link.labelKey)}
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 p-4 text-xs text-muted-foreground">
          <span>{t("settings.builtFor")}</span>
          <span className="font-semibold text-foreground">{t("settings.regionTag")}</span>
        </div>
      </CardContent>
    </Card>
  );
}
